import type { MorrisonsDay, MorrisonsPayload, MorrisonsWeek } from './report';

/*
 * Two fallbacks for when the live report is not available:
 *
 *   MR_PREVIEW — the design's sample figures ("Morrisons Disney 2026 — Campaign
 *     dashboard", a snapshot three weeks in: Tue 24 Nov, week 4 running). DEV
 *     ONLY, so the page can be reviewed against the design.
 *   MR_EMPTY   — the same structure with nothing measured. What a DEPLOYED build
 *     shows: a client is never served invented figures.
 *
 * Generated from the design's datasets. The design's data still carries
 * 100-points draws (a prize the campaign does not run, and no longer on the
 * page); they are left out here, so in the preview the prize counts add up to
 * fewer than the flowpacks opened.
 */

const GO_LIVE = '2026-11-02';
const END = '2026-12-24';
const PREVIEW_TODAY = '2026-11-24';
const DAY_MS = 86_400_000;
const iso = (offset: number) => new Date(Date.parse(`${GO_LIVE}T00:00:00Z`) + offset * DAY_MS).toISOString().slice(0, 10);
const TOTAL_DAYS = Math.round((Date.parse(`${END}T00:00:00Z`) - Date.parse(`${GO_LIVE}T00:00:00Z`)) / DAY_MS) + 1;
const TOTAL_WEEKS = Math.ceil(TOTAL_DAYS / 7);

// date, new cards, duplicate scans
const DAYS: Array<[string, number, number]> = [
  ['2026-11-02', 11868, 757],
  ['2026-11-03', 13899, 1078],
  ['2026-11-04', 14775, 1354],
  ['2026-11-05', 11774, 1250],
  ['2026-11-06', 15243, 1845],
  ['2026-11-07', 19460, 2653],
  ['2026-11-08', 16578, 2521],
  ['2026-11-09', 22059, 3710],
  ['2026-11-10', 20070, 3709],
  ['2026-11-11', 20600, 4159],
  ['2026-11-12', 20178, 4429],
  ['2026-11-13', 27472, 6528],
  ['2026-11-14', 23597, 6047],
  ['2026-11-15', 25449, 7011],
  ['2026-11-16', 27283, 8057],
  ['2026-11-17', 22063, 6967],
  ['2026-11-18', 24616, 8292],
  ['2026-11-19', 25811, 9257],
  ['2026-11-20', 25634, 9772],
  ['2026-11-21', 32073, 12973],
  ['2026-11-22', 27499, 11784],
  ['2026-11-23', 24103, 10929],
  ['2026-11-24', 15338, 7351],
];
// visitors, opt-ins, active users, new cards, duplicates, flowpacks granted, opened, lottery tickets, game plays, referrals, shares
const WEEKS: number[][] = [
  [171005, 59832, 45835, 103597, 11458, 132538, 116992, 72067, 1072, 5085, 12564],
  [98344, 23191, 51839, 159425, 35593, 143308, 127193, 78351, 3574, 1971, 4870],
  [84162, 19094, 63532, 184979, 67102, 171349, 152242, 93781, 6380, 1623, 4010],
  [17547, 4000, 16081, 39441, 18280, 41255, 36974, 22776, 1836, 340, 840],
];
// name, plays, wins, time's up, quits, players, players who won at least once
const GAMES: Array<[string, number, number, number, number, number, number]> = [
  ['Spot the Difference', 10061, 6237, 3193, 631, 5904, 4723],
  ['Photo Hunter', 1859, 1022, 657, 180, 1310, 852],
  ['Word Search', 644, 309, 295, 40, 482, 241],
  ['Picture Coloring', 298, 122, 158, 18, 236, 94],
];
const GAME_IDS = ['spot-the-difference', 'photo-hunter', 'word-search', 'picture-coloring'];
// new-card scans per card 001–120
const CARDS: number[] = [
  5766, 5449, 4330, 4455, 4352, 2712, 491, 4315, 4298, 2530, 4522, 2475, 5100, 2655, 2500, 3900, 4917, 2971, 3574, 2878, 3630, 4447, 3348, 5355, 4323, 4168,
  3168, 4905, 5226, 4535, 3180, 4969, 3324, 5013, 5153, 5019, 4342, 5680, 5550, 4721, 5100, 4484, 5254, 491, 5488, 3761, 3250, 4049, 5585, 3531, 3195, 4323,
  3292, 2511, 4925, 2858, 3983, 4911, 5344, 5598, 3132, 4294, 5361, 3662, 4409, 4355, 2588, 3802, 4385, 5216, 2873, 3584, 5046, 2682, 2528, 2744, 4242, 5125,
  5101, 3878, 4092, 5242, 3826, 4314, 2914, 4558, 5064, 491, 3711, 4074, 3251, 4037, 3806, 4441, 3469, 4324, 3386, 5673, 3199, 7655, 5026, 4936, 2788, 3351,
  4035, 3804, 5431, 5594, 4650, 4507, 3029, 3072, 491, 2828, 5596, 3971, 3597, 4972, 5601, 3450,
];

const blankDay = (date: string): MorrisonsDay => ({ date, newCards: null, duplicateScans: null });

const blankWeek = (week: number): MorrisonsWeek => ({
  week,
  start: iso((week - 1) * 7),
  end: iso(Math.min(week * 7 - 1, TOTAL_DAYS - 1)),
  status: 'planned',
  visitors: null,
  optIns: null,
  activeUsers: null,
  newCards: null,
  duplicateScans: null,
  flowpacksGranted: null,
  flowpacksOpened: null,
  lotteryTickets: null,
  gamePlays: null,
  referrals: null,
  shares: null,
});

export const MR_PREVIEW: MorrisonsPayload = {
  campaign: {
    name: 'Morrisons — Disney × Stickers 2026',
    start: GO_LIVE,
    end: END,
    timezone: 'Europe/London',
    live: true,
    currentWeek: 4,
    totalWeeks: TOTAL_WEEKS,
    collectionSize: 120,
    manualEntriesPerDay: 4,
    referralCap: 5,
    prizeCaps: { lottery: null, points500: 50000, points5000: 3000, freeFruit: 150000, freeBakingKit: 40000 },
  },
  totals: {
    uniqueVisitors: 215213,
    unsupportedDeviceViews: 15064,
    optIns: 106117,
    activeUsers: 95505,
    returningUsers: 45651,
    activeDays: 324717,
    dailyActiveAvg: 17149,
    newCards: 487442,
    duplicateScans: 132433,
    cameraCards: 423587,
    manualCards: 63855,
    cameraScans: 538471,
    manualScans: 81404,
    manualLimitUsers: 3124,
    collectors: 67914,
    milestones: [
      { cards: 1, users: 67914 },
      { cards: 12, users: 11189 },
      { cards: 30, users: 2124 },
      { cards: 60, users: 370 },
      { cards: 90, users: 124 },
      { cards: 120, users: 50 },
    ],
    flowpacksGranted: { daily: 428273, milestone12: 17591, share: 22284, referralInviter: 9019, referralInvitee: 9019, firstWin: 2264, welcome: 0, other: 0 },
    flowpacksOpened: 433401,
    prizes: { lottery: 266976, points500: 26004, points5000: 1733, freeFruit: 14085, freeBakingKit: 7585, other: 0 },
    gamePlays: 12862,
    uniquePlayers: 6148,
    gameWinners: 4980,
    referral: { codesShared: 4746, redeemed: 9019, inviters: 4746, invitersAtMax: 294, inviteeOptIns: 9019 },
    shares: 22284,
    sharers: 22284,
  },
  days: Array.from({ length: TOTAL_DAYS }, (_v, i) => {
    const date = iso(i);
    const d = DAYS.find((row) => row[0] === date);
    if (!d || date > PREVIEW_TODAY) return blankDay(date);
    return { date, newCards: d[1], duplicateScans: d[2] };
  }),
  weeks: Array.from({ length: TOTAL_WEEKS }, (_v, i) => {
    const w = WEEKS[i];
    if (!w) return blankWeek(i + 1);
    const [visitors, optIns, activeUsers, newCards, duplicateScans, flowpacksGranted, flowpacksOpened, lotteryTickets, gamePlays, referrals, shares] = w;
    return {
      ...blankWeek(i + 1),
      status: i === 3 ? 'live' : 'done',
      visitors,
      optIns,
      activeUsers,
      newCards,
      duplicateScans,
      flowpacksGranted,
      flowpacksOpened,
      lotteryTickets,
      gamePlays,
      referrals,
      shares,
    };
  }),
  cards: CARDS.map((collected, i) => ({ card: String(i + 1).padStart(3, '0'), collected })),
  games: GAMES.map(([name, plays, wins, timeUps, quits, players, winners], i) => ({
    id: GAME_IDS[i],
    name,
    position: i + 1,
    plays,
    wins,
    timeUps,
    quits,
    players,
    winners,
  })),
};

/** Same structure, nothing measured — the deployed no-live-data state. */
export const MR_EMPTY: MorrisonsPayload = {
  campaign: { ...MR_PREVIEW.campaign, live: false, currentWeek: 0 },
  totals: {
    uniqueVisitors: 0,
    unsupportedDeviceViews: null,
    optIns: 0,
    activeUsers: 0,
    returningUsers: 0,
    activeDays: 0,
    dailyActiveAvg: null,
    newCards: 0,
    duplicateScans: null,
    cameraCards: 0,
    manualCards: 0,
    cameraScans: null,
    manualScans: null,
    manualLimitUsers: 0,
    collectors: 0,
    milestones: MR_PREVIEW.totals.milestones.map((m) => ({ ...m, users: 0 })),
    flowpacksGranted: { daily: 0, milestone12: 0, share: 0, referralInviter: 0, referralInvitee: 0, firstWin: 0, welcome: 0, other: 0 },
    flowpacksOpened: 0,
    prizes: { lottery: 0, points500: 0, points5000: 0, freeFruit: 0, freeBakingKit: 0, other: 0 },
    gamePlays: 0,
    uniquePlayers: 0,
    gameWinners: 0,
    referral: { codesShared: 0, redeemed: 0, inviters: 0, invitersAtMax: 0, inviteeOptIns: 0 },
    shares: 0,
    sharers: 0,
  },
  days: MR_PREVIEW.days.map((d) => blankDay(d.date)),
  weeks: MR_PREVIEW.weeks.map((wk) => blankWeek(wk.week)),
  cards: MR_PREVIEW.cards.map((c) => ({ ...c, collected: 0 })),
  games: MR_PREVIEW.games.map((g) => ({ ...g, plays: 0, wins: 0, timeUps: 0, quits: 0, players: 0, winners: 0 })),
};
